/**
 * `/api/accounts/{id}/link-progress` — see `LinkProgressService` for the roster-floor rule
 * (`effective = max(stored, derived)`, computed server-side on every GET).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiSend, ApiError } from './api';
import { pushToast } from './events';
import type { LinkProgressDto } from './links';

export function linkProgressQueryKey(accountId: number) {
  return ['link-progress', accountId] as const;
}

export function useLinkProgress(accountId: number | null) {
  return useQuery({
    queryKey: linkProgressQueryKey(accountId ?? -1),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet<LinkProgressDto[]>(`/api/accounts/${accountId}/link-progress`, signal),
    enabled: accountId !== null,
  });
}

export function useUpsertLinkProgress(accountId: number) {
  const queryClient = useQueryClient();
  const key = linkProgressQueryKey(accountId);
  return useMutation({
    mutationFn: (vars: { classSlug: string; bracket: string }) =>
      apiSend<LinkProgressDto>('PUT', `/api/accounts/${accountId}/link-progress/${vars.classSlug}`, {
        bracket: vars.bracket,
      }),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<LinkProgressDto[]>(key);
      queryClient.setQueryData<LinkProgressDto[]>(key, (old) =>
        old?.map((p) =>
          p.classSlug === vars.classSlug
            ? {
                ...p,
                storedBracket: vars.bracket,
                // Optimistic effective: the real rank-max, using what we already know locally —
                // corrected by the server response/refetch either way via onSettled.
                effectiveBracket:
                  rankOf(vars.bracket) >= rankOf(p.derivedBracket) ? vars.bracket : p.derivedBracket,
              }
            : p,
        ),
      );
      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context) {
        queryClient.setQueryData(key, context.previous);
      }
      const message = error instanceof ApiError ? error.serverMessage : 'Could not save that link level.';
      pushToast(`${message} Your change was rolled back — try again.`);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: key }),
  });
}

const BRACKET_ORDER = ['NONE', 'L70', 'L120', 'L210'];
function rankOf(bracket: string): number {
  const index = BRACKET_ORDER.indexOf(bracket);
  return index < 0 ? 0 : index;
}
